import { Injectable } from '@angular/core';
import { DateService } from '../../date/date.service';
import { OperationService } from '../operation/operation.service';
import {
  DailyLogOperationInstance,
  DailyLogType,
  InitialOperationTimeHelper,
  LogComment,
  StepInstance,
  TimeDuration,
  TypeSwitch
} from '../../../models/projects/operation.models';
import { DailyLogService } from '../../daily-log/daily-log.service';
import { DateParserService } from '../../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { MultipleDayService } from '../../mutiple-day/multiple-day.service';
import { ContractualOperationValidatorService } from '../operation-validation/contractual-operation-validator-service/contractual-operation-validator.service';
import { cloneDeep } from 'lodash';
import { StartAndEnd } from '../../../models/projects/validation.models';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { DateTime } from 'luxon';

@Injectable()
export class OperationTimeService {

  constructor(
    private dateService: DateService,
    private operationService: OperationService,
    private dailyLogService: DailyLogService,
    private dateParserService: DateParserService,
    private multipleDayService: MultipleDayService,
    private contractualOperationValidatorService: ContractualOperationValidatorService,
    private timeZoneService: TimeZoneService,
  ) {
  }

  async setInitialTime(isConcurrent: boolean, dailyLogDate: PtLuxonDate, endOfDay: PtLuxonDate): Promise<InitialOperationTimeHelper> {
    const oldDateCopy = cloneDeep(dailyLogDate) as PtLuxonDate;
    let date = null;

    if (oldDateCopy && !isNaN(oldDateCopy.dateTime.toMillis())) {
      date = new PtLuxonDate(oldDateCopy.dateTime);
    }

    let latestTime = new PtLuxonDate();
    latestTime.dateTime = null as DateTime;

    if (!isConcurrent) {
      const contractualTimes = await this.contractualOperationValidatorService.getOtherContractualTimes(null, dailyLogDate, null, null, dailyLogDate);

      latestTime.dateTime = cloneDeep(oldDateCopy.dateTime);

      if (!oldDateCopy || !oldDateCopy.dateTime) {
        latestTime = await this.dailyLogService.getCurrentLogDay();
      }

      latestTime.dateTime = cloneDeep(latestTime.dateTime);

      for (let i = 0; i < contractualTimes.length; i++) {
        const time = contractualTimes[i];
        if (time.endTime.dateTime && time.endTime.dateTime > latestTime.dateTime) {
          latestTime = cloneDeep(time.endTime);
        }
      }

      if (contractualTimes.length < 1 || !latestTime) {
        return {ptLuxonDate: latestTime, first: true};
      }
    } else {
      const operation = await this.dailyLogService.mostRecentContractualOperation(dailyLogDate);

      if (!operation) {
        // oldDateCopy here will be the start of the day and will be local time, so set the local below, then remove the tz to set the UTC time as well
        latestTime = cloneDeep(oldDateCopy);
      } else {
        latestTime.dateTime = cloneDeep(operation.startTime.dateTime);
      }

      // if last operation is spanning from previous day, then dailyLogDate should be the minimum
      if (latestTime.dateTime < dailyLogDate.dateTime) {
        latestTime = cloneDeep(dailyLogDate);
      }
    }

    // if latest contractual time ends after end of day, then it will set the operation start to 00:00 on the next day,
    // and give them a validation error that it cannot start on the next day, but they then have no way of actually
    // putting it on the current day, so set it here to be the current day in these cases
    if (latestTime.dateTime >= endOfDay.dateTime) {
      latestTime = cloneDeep(dailyLogDate);
    }

    return {ptLuxonDate: latestTime, first: false};
  }

  async setDefaultTimings(operationInstance: DailyLogOperationInstance): Promise<DailyLogOperationInstance> {
    if (!operationInstance.stepCollectionInstance) {
      await this.multipleDayService.workOutEndDate(operationInstance, null, null, true);
      operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operationInstance.endTime);

      return operationInstance;
    }

    let latestTime = operationInstance.startTime;
    let mostRecentTime = operationInstance.startTime;

    for (const element of operationInstance.stepCollectionInstance.stepInstances) {
      latestTime = await this.setStepTimings(element, cloneDeep(latestTime), null);

      if (latestTime != null && latestTime.dateTime > mostRecentTime.dateTime) {
        mostRecentTime = latestTime;
      }
    }

    if (!operationInstance.endConfirmed) {
      operationInstance.endTime = cloneDeep(mostRecentTime);

      // add operation end delayed entry if it has been added for this step collection
      if (operationInstance.projectSpecificOperation.operationSummary.stepCollection
        && operationInstance.projectSpecificOperation.operationSummary.stepCollection.operationEndDelayedEntrySeconds) {
        operationInstance.endTime.dateTime = await this.dateService.addDuration(operationInstance.endTime.dateTime,
          {seconds: operationInstance.projectSpecificOperation.operationSummary.stepCollection.operationEndDelayedEntrySeconds});
      }

      operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(operationInstance.endTime);
    }

    return operationInstance;
  }

  setNullTimings(operationInstance: DailyLogOperationInstance): DailyLogOperationInstance {
    operationInstance.startTime = new PtLuxonDate();
    operationInstance.startTime.setDate(null);
    operationInstance.endTime = new PtLuxonDate();
    operationInstance.endTime.setDate(null);

    if (operationInstance.stepCollectionInstance) {
      operationInstance.stepCollectionInstance.stepInstances.forEach(element => {
        element.startTime = new PtLuxonDate();
        element.startTime.setDate(null);
        element.endTime = new PtLuxonDate();
        element.endTime.setDate(null);
      });
    }

    return operationInstance;
  }

  async setStepTimings(element: StepInstance, latestTime: PtLuxonDate, skipStart?: boolean): Promise<PtLuxonDate> {
    if (latestTime == null) {
      if (!element.startConfirmed && element.startTime) {
        element.startTime.setDate(null);
        element.startTimeToShow = null;
      }

      if (!element.endConfirmed && element.endTime) {
        element.endTime.setDate(null);
        element.endTimeToShow = null;
      }
      return null;
    }

    // skipStart will be true if user has edited step start of transit (this is needed because transit doesn't confirm step times by editing them, so it
    // messes with the step time automation and adds stepInstance.delaySeconds when they shouldn't be)
    if (!element.startConfirmed && !skipStart) {
      if (latestTime && latestTime.dateTime) {
        if (element.secondsDelay || element.secondsDelay === 0) {
          element.startTime = new PtLuxonDate(await this.dateService.addDuration(latestTime.dateTime, {second: element.secondsDelay}));
        } else {
          element.startTime = latestTime;
        }

        await element.startTime.checkForMultipleOffsets(this.timeZoneService, element.startTime.dateTime ? element.startTime.dateTime.offset / 60 : null);
        element.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(element.startTime);
      } else {
        element.startTime.setDate(null);
        element.startTimeToShow = null;
      }
    }

    if (!element.endConfirmed) {
      if ((element.secondsSpan || element.secondsSpan === 0) && element.startTime) {
        if (element.startTime.getDate()) {
          element.endTime = new PtLuxonDate(await this.dateService.addDuration(element.startTime.dateTime, {second: element.secondsSpan}));
        } else {
          element.endTime.setDate(null);
          element.endTimeToShow = null;
        }
      } else {
        element.endTime = element.startTime;
      }

      await element.endTime.checkForMultipleOffsets(this.timeZoneService, element.endTime.dateTime ? element.endTime.dateTime.offset / 60 : null);
      element.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(element.endTime);
    }

    return cloneDeep(element.endTime);
  }

  removeTime(
    stepInstances: StepInstance[],
    operationInstance: DailyLogOperationInstance,
    stepInstance?: StepInstance,
    stepPosition?: number
  ) {
    stepPosition++;

    if (operationInstance != null) {
      operationInstance.endTime.setDate(null);
      operationInstance.endTimeToShow = null;
    }

    if (stepInstance != null) {
      stepInstance.endTime.setDate(null);
      stepInstance.endTimeToShow = null;

      const futureSteps = stepInstances.slice(stepPosition, stepInstances.length);

      for (let i = 0; i < futureSteps.length; i++) {

        if (futureSteps[i].exclude) {
          continue;
        }

        if (!futureSteps[i].startConfirmed && !futureSteps[i].endConfirmed) {
          futureSteps[i].startTime.setDate(null);
          futureSteps[i].startTimeToShow = null;
          futureSteps[i].startTimeOffset = null;

          futureSteps[i].endTime.setDate(null);
          futureSteps[i].endTimeToShow = null;
          futureSteps[i].endTimeOffset = null;
          continue;
        }
        break;
      }

      if (!operationInstance.endConfirmed) {
        operationInstance.endTime.setDate(null);
        operationInstance.endTimeToShow = null;
        operationInstance.endTimeOffset = null;
      }
    }

  }

  async updateFutureSteps(
    position: number,
    latestTime: PtLuxonDate,
    operationInstance: DailyLogOperationInstance,
    stepInstances: StepInstance[],
    startChanged: boolean,
    minimumDate?: DateTime,
    canConfirmTimes?: boolean
  ): Promise<DailyLogOperationInstance> {
    if (position > 0 && startChanged) {
      position--;
    }

    const futureSteps = stepInstances.slice(position, stepInstances.length);

    for (let i = 0; i < futureSteps.length; i++) {

      if (futureSteps[i].exclude) {
        continue;
      }

      if (futureSteps[i].startConfirmed && i !== 0) {
        break;
      }

      if (startChanged && futureSteps[i].endConfirmed && i === 0) {
        break;
      }

      latestTime = await this.setStepTimings(futureSteps[i], cloneDeep(latestTime), (i === 0 && canConfirmTimes !== null && canConfirmTimes === false));
      if (latestTime.dateTime < minimumDate) {
        latestTime.setDate(minimumDate);
      }
    }

    if (stepInstances.length < 1) {
      if (!operationInstance.endConfirmed) {
        operationInstance.endTimeToShow = operationInstance.startTimeToShow;
      }

      return operationInstance;
    }

    let mostRecentTime = stepInstances.find(p => true).endTime;

    for (let i = 0; i < stepInstances.length; i++) {

      if (stepInstances[i].exclude) {
        continue;
      }

      if (stepInstances[i].endTime.dateTime > mostRecentTime.getDate()) {
        mostRecentTime = stepInstances[i].endTime;
      }
    }

    if (!operationInstance.endConfirmed) {

    }

    const foundNull = stepInstances.find(p => !p.endTime.dateTime && p.exclude === false);

    if (!operationInstance.endConfirmed) {
      if (foundNull) {
        operationInstance.endTimeToShow = null;
        operationInstance.endTime.setDate(null);
      } else {
        operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(mostRecentTime);
        operationInstance.endTime = cloneDeep(mostRecentTime);
        // check needed
        // await operationInstance.endTime.setLocalDate(this.timeZoneService, false, null, operationInstance.endTimeOffset);
      }
    }

    return operationInstance;
  }

  adjustAllEstimates(instance: DailyLogOperationInstance, position?: number) {
    if (!instance) {
      return;
    }

    // we pass in the 1 index based position, but we need to convert it to 0 index based
    if (position) {
      position--;
    }

    const startTime = instance.startTime.dateTime;
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return;
    }

    for (let i = position || 0; i < steps.length; i++) {

      const stepStartTime = steps[i].startTime.dateTime;
      const stepEndTime = steps[i].endTime.dateTime;
      const previousStepPosition = this.multipleDayService.findAdjacentStep(instance, i, true);
      const previousStep = previousStepPosition != null ? steps[previousStepPosition] : null;
      const previousStepEndTime = !previousStep ? null : previousStep.endTime.dateTime;

      if (i === 0) {
        if (!!startTime && !isNaN(startTime.toMillis()) && !!stepStartTime && !isNaN(stepStartTime.toMillis())) {
          const newDelay = (stepStartTime.toMillis() - startTime.toMillis()) / 1000;
          steps[i].secondsDelay = newDelay;
        }

        if (!!stepStartTime && !isNaN(stepStartTime.toMillis()) && !!stepEndTime && !isNaN(stepEndTime.toMillis())) {
          const newSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
          steps[i].secondsSpan = newSpan;
        }
      } else {
        if (!!previousStepEndTime && !isNaN(previousStepEndTime.toMillis()) && !!stepStartTime && !isNaN(stepStartTime.toMillis())) {
          const newDelay = (stepStartTime.toMillis() - previousStepEndTime.toMillis()) / 1000;
          steps[i].secondsDelay = newDelay;
        }

        if (!!stepStartTime && !isNaN(stepStartTime.toMillis()) && !!stepEndTime && !isNaN(stepEndTime.toMillis())) {
          const newSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
          steps[i].secondsSpan = newSpan;
        }
      }
    }
  }

  adjustEstimates(instance: DailyLogOperationInstance, type: DailyLogType, isStart?: boolean, position?: number) {
    switch (type) {
      case DailyLogType.OperationStart:
        this.adjustStartEstimates(instance);
        break;
      case DailyLogType.Step:
        this.adjustStepEstimates(instance, isStart, position);
        break;
    }
  }

  private adjustStartEstimates(instance: DailyLogOperationInstance) {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;
    let selectedTime = null;

    if (!steps) {
      return;
    }

    const firstStepPosition = this.multipleDayService.findAdjacentStep(instance, -1);

    if (firstStepPosition == null) {
      return;
    }

    const selectedStep = instance.stepCollectionInstance.stepInstances[firstStepPosition];
    selectedTime = selectedStep.startTime.dateTime;

    if (!selectedTime) {
      return;
    }

    const startTime = instance.startTime.dateTime;

    if (!startTime) {
      return;
    }

    selectedStep.secondsDelay = (selectedTime.getTime() - startTime.toMillis()) / 1000;
  }

  private adjustStepEstimates(instance: DailyLogOperationInstance, isStart: boolean, position: number) {
    if (isStart) {
      this.adjustStepStartEstimates(instance, position);
    } else {
      this.adjustStepEndEstimates(instance, position);
    }
  }

  private adjustStepStartEstimates(instance: DailyLogOperationInstance, position: number) {
    const operationStartTime = instance.startTime.dateTime;
    const steps = instance.stepCollectionInstance.stepInstances;
    const step = steps[position];
    const previousStepPosition = this.multipleDayService.findAdjacentStep(instance, position, true);
    const previousStep = previousStepPosition != null ? steps[previousStepPosition] : null;
    const previousStepEndTime = !previousStep ? null : previousStep.endTime.dateTime;
    const stepStartTime = step.startTime.dateTime;
    const stepEndTime = step.endTime.dateTime;

    if (!previousStep) {
      if (!stepStartTime) {
        return;
      }

      if (stepStartTime.toMillis() - operationStartTime.toMillis() > 0) {
        step.secondsDelay = (stepStartTime.toMillis() - operationStartTime.toMillis()) / 1000;
      }

      if (!stepEndTime) {
        return;
      }

      if (stepEndTime.toMillis() - stepStartTime.toMillis() > 0) {
        step.secondsSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
      }
    } else {
      if (!stepStartTime) {
        return;
      }

      if (!previousStepEndTime) {
        return;
      }

      if (stepStartTime.toMillis() - previousStepEndTime.toMillis() > 0) {
        step.secondsDelay = (stepStartTime.toMillis() - previousStepEndTime.toMillis()) / 1000;
      }

      if (!stepEndTime) {
        return;
      }

      if (stepEndTime.toMillis() - stepStartTime.toMillis() > 0) {
        step.secondsSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
      }
    }
  }

  private adjustStepEndEstimates(instance: DailyLogOperationInstance, position: number) {
    const steps = instance.stepCollectionInstance.stepInstances;
    const step = steps[position];

    const nextStepPosition = this.multipleDayService.findAdjacentStep(instance, position);
    const nextStep = nextStepPosition != null ? steps[nextStepPosition] : null;
    const nextStepStartTime = !nextStep ? null : nextStep.startTime.dateTime;
    const stepStartTime = step.startTime.dateTime;
    const stepEndTime = step.endTime.dateTime;

    if (!nextStep) {
      if (!stepStartTime) {
        return;
      }

      if (!stepEndTime) {
        return;
      }


      if (stepEndTime.toMillis() - stepStartTime.toMillis() > 0) {
        step.secondsSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
      }
    } else {
      if (!stepStartTime) {
        return;
      }

      if (!nextStepStartTime) {
        return;
      }

      if (nextStepStartTime.toMillis() - stepEndTime.toMillis() > 0) {
        nextStep.secondsDelay = (nextStepStartTime.toMillis() - stepEndTime.toMillis()) / 1000;
      }

      if (!stepEndTime) {
        return;
      }

      if (stepEndTime.toMillis() - stepStartTime.toMillis() > 0) {
        step.secondsSpan = (stepEndTime.toMillis() - stepStartTime.toMillis()) / 1000;
      }
    }
  }

  // when in implementer mode - if user doesn't have correct permissions then we need to disable times on future days so they can't be edited
  async setLockedDayDisabledFlags(instance: DailyLogOperationInstance, lockedDayPermissions: LockedDayPermissions, endOfDay: PtLuxonDate) {
    if (lockedDayPermissions.functionalPermissions.canImpactFutureDay) {
      return;
    }

    if (instance.stepCollectionInstance) {
      for (let i = 0; i < instance.stepCollectionInstance.stepInstances.length; i++) {
        const step = instance.stepCollectionInstance.stepInstances[i];

        if (step.startTime && step.startTime.dateTime >= endOfDay.dateTime) {
          step.startDisabledForLockedDay = true;
        }

        if (step.endTime && step.endTime.dateTime >= endOfDay.dateTime) {
          step.endDisabledForLockedDay = true;
        }
      }
    }

    if (instance.endTime && instance.endTime.dateTime) {
      if (instance.endTime.dateTime >= endOfDay.dateTime) {
        instance.endDisabledForLockedDay = true;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/operations/operation-time/operation-time.service.ts